/**
 * Feedback Store
 * Manages user feedback on validation findings with localStorage persistence
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { FindingFeedback, FeedbackType, FeedbackStats, FeedbackByRule } from '@/types/feedback';

interface FeedbackState {
  feedbacks: Map<string, FindingFeedback>;

  // Actions
  setFeedback: (
    findingId: string,
    ruleId: string,
    elementId: number,
    feedback: FeedbackType,
    notes?: string
  ) => void;

  getFeedback: (findingId: string) => FindingFeedback | undefined;

  removeFeedback: (findingId: string) => void;

  clearAllFeedback: () => void;

  // Statistics
  getOverallStats: () => FeedbackStats;

  getStatsByRule: () => FeedbackByRule;

  getRuleStats: (ruleId: string) => FeedbackStats;

  // Export
  exportFeedbackData: () => FindingFeedback[];
}

const calculateStats = (feedbacks: FindingFeedback[]): FeedbackStats => {
  const total = feedbacks.length;
  const correct = feedbacks.filter(f => f.feedback === 'correct').length;
  const incorrect = feedbacks.filter(f => f.feedback === 'incorrect').length;
  const pending = feedbacks.filter(f => f.feedback === null).length;
  const accuracy = total > 0 ? (correct / total) * 100 : 0;

  return { total, correct, incorrect, pending, accuracy };
};

export const useFeedbackStore = create<FeedbackState>()(
  persist(
    (set, get) => ({
      feedbacks: new Map(),

      setFeedback: (findingId, ruleId, elementId, feedback, notes) => {
        set((state) => {
          const newFeedbacks = new Map(state.feedbacks);

          newFeedbacks.set(findingId, {
            findingId,
            ruleId,
            elementId,
            feedback,
            timestamp: new Date(),
            notes,
          });

          return { feedbacks: newFeedbacks };
        });
      },

      getFeedback: (findingId) => {
        return get().feedbacks.get(findingId);
      },

      removeFeedback: (findingId) => {
        set((state) => {
          const newFeedbacks = new Map(state.feedbacks);
          newFeedbacks.delete(findingId);
          return { feedbacks: newFeedbacks };
        });
      },

      clearAllFeedback: () => {
        set({ feedbacks: new Map() });
      },

      getOverallStats: () => {
        const feedbacks = Array.from(get().feedbacks.values());
        return calculateStats(feedbacks);
      },

      getStatsByRule: () => {
        const feedbacks = Array.from(get().feedbacks.values());
        const byRule: FeedbackByRule = {};

        feedbacks.forEach((feedback) => {
          if (!byRule[feedback.ruleId]) {
            byRule[feedback.ruleId] = {
              total: 0,
              correct: 0,
              incorrect: 0,
              pending: 0,
              accuracy: 0,
            };
          }
        });

        Object.keys(byRule).forEach((ruleId) => {
          const ruleFeedbacks = feedbacks.filter(f => f.ruleId === ruleId);
          byRule[ruleId] = calculateStats(ruleFeedbacks);
        });

        return byRule;
      },

      getRuleStats: (ruleId) => {
        const feedbacks = Array.from(get().feedbacks.values())
          .filter(f => f.ruleId === ruleId);
        return calculateStats(feedbacks);
      },

      exportFeedbackData: () => {
        return Array.from(get().feedbacks.values());
      },
    }),
    {
      name: 'herocon-feedback-storage',
      // Custom storage to handle Map serialization
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;

          const { state } = JSON.parse(str);
          return {
            state: {
              ...state,
              feedbacks: new Map(Object.entries(state.feedbacks || {})),
            },
          };
        },
        setItem: (name, value) => {
          const feedbacksObj = Object.fromEntries(value.state.feedbacks);
          localStorage.setItem(
            name,
            JSON.stringify({
              state: {
                ...value.state,
                feedbacks: feedbacksObj,
              },
            })
          );
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);
